import React from "react";
import PropTypes from "prop-types";
import { MODEL_FAMILIES } from "../../utils/constants";

/**
 * Format cents as a dollar string (e.g. 1000 → "$10.00").
 * Note: credit packages use CENTS (integer) — unlike plans.price which is DOLLARS.
 */
function formatPrice(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Buy Credits section for the Settings page.
 * Displays available credit packages grouped by model family.
 */
const CreditPackages = ({
  packages = [],
  loading = false,
  purchasing = null,
  onBuy,
}) => {
  // Fix #8: Don't render loading skeleton if we'll just hide the section anyway.
  // Return null early — the section appears once data arrives (no flicker).
  if (loading || !packages || packages.length === 0) {
    return null;
  }

  // Group packages by model family, preserving MODEL_FAMILIES order
  const grouped = MODEL_FAMILIES.map((family) => ({
    family,
    items: packages.filter((pkg) => pkg.modelFamily === family.key),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="settings-card credit-packages-section">
      <h3>Buy Credits</h3>
      <p className="credit-packages-subtitle">
        Purchase additional credits. Purchased credits never expire.
      </p>

      <div className="credit-packages-grid">
        {grouped.map(({ family, items }) => (
          <div key={family.key} className="credit-packages-family">
            <h4
              className="credit-packages-family-label"
              style={{ color: family.color }}
            >
              {family.label}
            </h4>
            <div className="credit-packages-list">
              {items.map((pkg) => {
                const isPurchasing = purchasing === pkg.id;
                const isOtherPurchasing = purchasing && purchasing !== pkg.id;
                const isDisabled = isPurchasing || isOtherPurchasing;

                // Fix #9: Differentiate button text for the active purchase vs blocked siblings
                let buttonText = "Buy";
                if (isPurchasing) {
                  buttonText = "Processing...";
                } else if (isOtherPurchasing) {
                  buttonText = "Wait...";
                }

                return (
                  <div key={pkg.id} className="credit-package-card">
                    <div className="credit-package-info">
                      <span className="credit-package-amount">
                        {pkg.creditAmount} credits
                      </span>
                      {pkg.name && (
                        <span className="credit-package-name">{pkg.name}</span>
                      )}
                    </div>
                    <div className="credit-package-action">
                      <span className="credit-package-price">
                        {formatPrice(pkg.priceCents)}
                      </span>
                      <button
                        className="credit-package-buy-btn"
                        onClick={() => onBuy(pkg)}
                        disabled={isDisabled}
                        aria-label={`Buy ${pkg.creditAmount} ${family.label} credits for ${formatPrice(pkg.priceCents)}`}
                      >
                        {buttonText}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

CreditPackages.propTypes = {
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      modelFamily: PropTypes.string.isRequired,
      creditAmount: PropTypes.number.isRequired,
      priceCents: PropTypes.number.isRequired,
      name: PropTypes.string,
      // Fix #1: stripePriceId removed — it's a Stripe implementation detail
      // not used by this component and shouldn't be advertised to the client.
    }),
  ),
  loading: PropTypes.bool,
  purchasing: PropTypes.number,
  onBuy: PropTypes.func.isRequired,
  // Fix #6/#15: toast prop removed — all error toasts are handled by
  // the parent (Settings.js handleBuyCredits). Was declared .isRequired
  // but never referenced in the component body.
};

// Fix #16: defaultProps removed — defaults are now in the destructuring
// signature above (React 18.3+ deprecates defaultProps on function components).

export default CreditPackages;
